import {createSlice} from '@reduxjs/toolkit';
import get from 'lodash.get';
import httpClient from '../../utils/httpClient';
import {
    fetchSessionInfo, fetchUserAvatar, setUserAvatar, saveUserAttributes
} from '../Login/userSlice';
import {BASE_PATH} from '../../../constants';
import {setIntroductionInfo} from '../appSlice';
import {getErrorMessage} from '../../utils/requests/error';
import {parseJWT} from '../../utils/jwtUtils';
import {userApi} from '../../../api/user/api';
import {accountUserApi} from '../../../api/accountUser/api';
import {getAccount} from '../../../api/account/api';

export const accountSettingsSlice = createSlice({
    name: 'accountSettings',
    initialState: {
        isAvatarSaving: false,
        avatarSavingError: null,
        isPersonalInfoSaving: false,
        personalInfoSaveError: null,
        subaccounts: [],
        accessibleSubaccounts: []
    },
    reducers: {
        setIsAvatarSaving: (state, action) => {
            state.isAvatarSaving = action.payload;
        },
        setAvatarSavingError: (state, action) => {
            state.avatarSavingError = action.payload;
        },
        setIsPersonalInfoSaving: (state, action) => {
            state.isPersonalInfoSaving = action.payload;
        },
        setPersonalInfoSaveError: (state, action) => {
            state.personalInfoSaveError = action.payload;
        },
        setSubaccounts: (state, action) => {
            state.subaccounts = action.payload;
        },
        setAccessibleSubaccounts: (state, action) => {
            state.accessibleSubaccounts = action.payload;
        }
    }
});

export const {
    setIsAvatarSaving,
    setAvatarSavingError,
    setIsPersonalInfoSaving,
    setPersonalInfoSaveError,
    setSubaccounts,
    setAccessibleSubaccounts
} = accountSettingsSlice.actions;

export const saveAvatar = ({
    avatar
}) => async (dispatch, getState) => {
    const {user} = getState();
    dispatch(setIsAvatarSaving(true));
    dispatch(setAvatarSavingError(null));

    try {
        const formData = new FormData();
        formData.append('avatar', avatar);

        const result = await userApi.createAvatar({
            userId: user.user.id,
            data: formData
        });
        await dispatch(fetchUserAvatar({userId: user.user.id}));
        dispatch(setIsAvatarSaving(false));
        dispatch(setAvatarSavingError(null));
        return result.data;
    } catch (err) {
        dispatch(setIsAvatarSaving(false));
        dispatch(setAvatarSavingError(getErrorMessage(err)));
        return false;
    }
};

export const removeAvatar = () => async (dispatch, getState) => {
    const {user} = getState();
    dispatch(setIsAvatarSaving(true));
    dispatch(setAvatarSavingError(null));

    try {
        await userApi.deleteAvatar({userId: user.user.id});
        dispatch(setUserAvatar(null));
        dispatch(setIsAvatarSaving(false));
        dispatch(setAvatarSavingError(null));
        return true;
    } catch (err) {
        dispatch(setIsAvatarSaving(false));
        dispatch(setAvatarSavingError(getErrorMessage(err)));
        return false;
    }
};

export const saveUser = (data) => async (dispatch, getState) => {
    const {user} = getState();

    dispatch(setIsPersonalInfoSaving(true));
    dispatch(setPersonalInfoSaveError(null));

    try {
        await httpClient.post(`${BASE_PATH}/proxy/account/senders/${user.user.id}`, data);

        dispatch(fetchSessionInfo());
        dispatch(setIsPersonalInfoSaving(false));
        dispatch(setPersonalInfoSaveError(null));
        return true;
    } catch (err) {
        dispatch(setIsPersonalInfoSaving(false));
        dispatch(setPersonalInfoSaveError(getErrorMessage(err)));
        return false;
    }
};

export const startIntroduction = () => async (dispatch) => {
    const wasSuccessful = await dispatch(saveUserAttributes({show_intro: true}));
    return wasSuccessful;
};

export const finishIntroduction = () => async (dispatch) => {
    const wasSuccessful = await dispatch(saveUserAttributes({show_intro: false}));
    if (wasSuccessful) {
        dispatch(setIntroductionInfo({
            stepIndex: null,
            validation: {
                transactionId: null
            }
        }));
    }
    return wasSuccessful;
};

export const fetchSubaccounts = () => async (dispatch) => {
    const url = `${BASE_PATH}/proxy/account/subaccounts`;

    try {
        const result = await httpClient.get(url);
        dispatch(setSubaccounts(get(result, 'data', [])));
        return result.data;
    } catch (err) {
        return false;
    }
};

export const fetchAccessibleSubaccounts = () => async (dispatch, getState) => {
    const {user} = getState();
    const externalId = parseJWT(user.accessToken)?.external_id;
    const userId = externalId || user.user.id;
    try {
        const accessibleResult = await accountUserApi.getUserAccessibleAccounts({
            userId,
            params: {status: 'active'}
        });
        const accountIds = get(accessibleResult, 'data', []).map((entry) => entry.account_id);
        const accountResponses = await Promise.all(accountIds.map((accountId) => getAccount(accountId)));
        const accessibleAccounts = accountResponses.map((response) => response.data).filter(Boolean);
        dispatch(setAccessibleSubaccounts(accessibleAccounts));

        return accessibleAccounts;
    } catch (err) {
        return false;
    }
};

export const changeSubaccountId = (subaccountId) => async () => {
    const url = `${BASE_PATH}/proxy/session/activeAccount`;
    const options = {
        headers: {'content-type': 'application/text'}
    };

    try {
        await httpClient.post(url, subaccountId, options);
        return true;
    } catch (err) {
        return false;
    }
};

export const selectIsAvatarSaving = (state) => state.accountSettings.isAvatarSaving;
export const selectAvatarSavingError = (state) => state.accountSettings.avatarSavingError;
export const selectIsPersonalInfoSaving = (state) => state.accountSettings.isPersonalInfoSaving;
export const selectPersonalInfoSaveError = (state) => state.accountSettings.personalInfoSaveError;
export const selectSubaccounts = (state) => state.accountSettings.subaccounts;
export const selectAccessibleAccounts = (state) => state.accountSettings.accessibleSubaccounts;

export default accountSettingsSlice.reducer;
